import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  createScriptCoCustomer,
  editScriptCoCustomer,
  searchScriptCoProducts,
  createScriptCoOrder,
  getScriptCoOrderDetails,
  addScriptCoOrderNote,
} from "../../../api/scriptco";
import { fetchMemberOverview } from "../../../api/priority";
import ScriptCoMemberDetails from "./ScriptCoMemberDetails";
import ScriptCoAddressDetails from "./ScriptCoAddressDetails";
import ScriptCoOrderDetailsForm from "./ScriptCoOrderDetailsForm";
import ScriptCoPrescriptionOpportunity from "./ScriptCoPrescriptionOpportunity";
import CommonDialog from "../../common/CommonDialog";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

function CustomStepIcon(props) {
  const { active, completed } = props;

  let borderStyle;
  if (completed) {
    borderStyle = "none";
  } else if (active) {
    borderStyle = "3.5px solid #403C6F";
  } else {
    borderStyle = "2px solid #C4C2D3";
  }
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        boxSizing: "border-box",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: completed ? "#403C6F" : "#F7F8FB",
        border: borderStyle,
        zIndex: 1,
      }}
    >
      {completed && (
        <CheckIcon
          sx={{
            color: "#FFFFFF",
            fontSize: "10px",
            stroke: "#FFFFFF",
            strokeWidth: 1,
          }}
        />
      )}
    </Box>
  );
}

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
};

const steps = ["Customer Info", "Order Details", "Prescription"];

const toLabelContactMethod = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "mobilephone" || normalized === "sms")
    return "Mobile Phone";
  if (normalized === "email") return "Email";
  if (normalized === "alternatephone" || normalized === "homephone")
    return "Alternate Phone";
  if (normalized === "alternateemail") return "Alternate Email";
  return "";
};

const getAvailableContactMethods = (info) => {
  const available = [];
  if ((info.mobileNumber || "").trim()) {
    available.push("Mobile Phone");
  }
  if ((info.alternatePhone || "").trim()) {
    available.push("Alternate Phone");
  }
  return available;
};

const determinePreferredMethod = (info, availableMethods, currentPreferred) => {
  const priorityOrder = ["Mobile Phone", "Alternate Phone"];
  if (!currentPreferred || !availableMethods.includes(currentPreferred)) {
    for (const method of priorityOrder) {
      if (availableMethods.includes(method)) {
        return method;
      }
    }
    return availableMethods[0] || "Mobile Phone";
  }
  return currentPreferred;
};

const splitToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const cleanPhoneNumber = (phone) => {
  if (!phone) return "";
  const originalPhone = String(phone).trim();

  // If it starts with '+', assume international and keep the '+' while removing other non-digits
  if (originalPhone.startsWith("+")) {
    return "+" + originalPhone.substring(1).replace(/\D/g, "");
  } else {
    // Otherwise, assume North American and remove all non-digits
    return originalPhone.replace(/\D/g, "");
  }
};

const normalizeSex = (value) => {
  if (!value) return "";
  const lowerValue = String(value).toLowerCase();
  if (lowerValue.includes("female")) return "Female";
  if (lowerValue.includes("male")) return "Male";
  return "";
};

const boolToYesNo = (value, defaultValue = "") => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return defaultValue;
};

const yesNoToBool = (value, defaultValue = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "yes";
  return defaultValue;
};

const toCsv = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  return value || "";
};

const getNameParts = (member = {}) => {
  const fullName = (member.fullName || member.name || "").trim();
  const split = fullName ? fullName.split(" ").filter(Boolean) : [];
  return {
    firstName: member.firstName || split[0] || "Some",
    lastName: member.lastName || split.slice(1).join(" ") || "",
  };
};

const convertToIsoDate = (dateStr) => {
  if (!dateStr) return null;
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [m, d, y] = trimmed.split("/");
    return `${y}-${m}-${d}`;
  }
  return null;
};

const convertToMmDdYyyy = (dateStr) => {
  if (!dateStr) return "";
  const trimmed = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split("-");
    return `${m}/${d}/${y}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    return trimmed;
  }
  return "";
};

const resolveIsBillingAddress = (formData, scriptCoDetails) => {
  if (formData?.scriptCoDetails?.isBillingAddress !== undefined) {
    return !!formData.scriptCoDetails.isBillingAddress;
  }
  if (scriptCoDetails.isBillingAddress !== undefined) {
    return !!scriptCoDetails.isBillingAddress;
  }
  return false;
};

const resolveIsBillingAddressV2 = (customerDetails) => {
  if (customerDetails.isBilling !== undefined) {
    return customerDetails.isBilling;
  }
  if (customerDetails.isBillingAddress !== undefined) {
    return customerDetails.isBillingAddress;
  }
  return false;
};

const pickDefined = (value, fallback) =>
  value === undefined ? fallback : value;

const pickNonEmpty = (value, fallback) =>
  value === undefined || value === "" ? fallback : value;

const resolveBaseMemberId = (member, opportunity) => {
  let fallbackMemberId = null;
  if (
    opportunity?.memberId !== opportunity?.id &&
    opportunity?.memberId !== opportunity?.opportunityId
  ) {
    fallbackMemberId = opportunity?.memberId;
  }
  return (
    member.memberId ||
    member.id ||
    opportunity?.member?.memberId ||
    opportunity?.member?.id ||
    fallbackMemberId ||
    null
  );
};

const buildCreateScriptCoPayload = (formData, opportunity) => {
  const member = opportunity?.member || {};
  const existing =
    opportunity?.scriptCoDetails || opportunity?.grmDetails || {};
  const existingCustomer = existing.customerDetails || {};
  const { firstName, lastName } = getNameParts(member);

  const billing = formData?.address?.billing || {};
  const delivery = formData?.address?.delivery || {};
  const medical = formData?.orderDetails?.medical || {};
  const general = formData?.address?.general || {};
  const addressNotes = formData?.address?.scriptCoDetails?.patientNotes;
  const orderNotes = formData?.orderDetails?.scriptCoDetails?.patientNotes;
  const rootNotes = formData?.scriptCoDetails?.patientNotes;

  const hasValue = (val) => val !== undefined && val !== "";

  const getResolvedPatientNotes = (
    addressNotes,
    orderNotes,
    rootNotes,
    existingCustomer,
  ) => {
    if (hasValue(addressNotes)) return addressNotes;
    if (hasValue(orderNotes)) return orderNotes;
    if (hasValue(rootNotes)) return rootNotes;
    return existingCustomer.PatientNotes || existingCustomer.patientNotes || "";
  };

  const resolvedPatientNotes = getResolvedPatientNotes(
    addressNotes,
    orderNotes,
    rootNotes,
    existingCustomer,
  );

  const scriptCoDetails = {
    ...formData?.scriptCoDetails,
    ...formData?.address?.scriptCoDetails,
    ...formData?.orderDetails?.scriptCoDetails,
    patientNotes: resolvedPatientNotes,
  };
  const patientDoc =
    formData?.address?.patientDocument || formData?.patientDocument;

  const baseMemberId = resolveBaseMemberId(member, opportunity);
  const rawMemberId = baseMemberId || null;
  const parsedMemberId =
    Number.parseInt(String(rawMemberId).replace(/\D/g, ""), 10) || null;

  const rawDob =
    member.dateOfBirth ||
    member.dob ||
    existingCustomer.dateOfBirth ||
    existingCustomer.Dob;
  const formattedDob = convertToMmDdYyyy(rawDob) || "01/10/1976";

  const rawGender =
    medical.sex ||
    existingCustomer.sex ||
    existingCustomer.Gender ||
    member.sex ||
    member.gender;
  const gender = normalizeSex(rawGender) || "Female";

  const addressLine1 =
    delivery.street || billing.street || existingCustomer.AddressLine1 || "";
  const addressLine2 =
    delivery.line || billing.line || existingCustomer.AddressLine2 || null;
  const cityName =
    delivery.city || billing.city || existingCustomer.CityName || "";
  const stateCode =
    delivery.state || billing.state || existingCustomer.StateCode || "";
  const zipCode = (
    delivery.zip ||
    billing.zip ||
    existingCustomer.ZipCode ||
    ""
  )
    .replace(/\D/g, "")
    .slice(0, 5);

  const defaultMemberId =
    baseMemberId ||
    member.subscriberNum ||
    opportunity?.subscriberNum ||
    existingCustomer.PatientId ||
    "";
  const defaultGroupName =
    existingCustomer.patientGroup ||
    existingCustomer.PatientGroup ||
    member.groupName ||
    member.groupNum ||
    member.groupNumber ||
    member.group ||
    opportunity?.groupName ||
    opportunity?.groupNum ||
    opportunity?.groupNumber ||
    opportunity?.group ||
    "";

  const patientId = pickDefined(scriptCoDetails.patientId, defaultMemberId);
  const patientGroup = pickDefined(
    scriptCoDetails.patientGroup,
    defaultGroupName,
  );

  const patientDocPayload = patientDoc?.base64
    ? {
        Content: patientDoc.base64,
        FileName: patientDoc.fileName || "proof.png",
      }
    : null;

  const payload = {
    // Exact fields matching AddNewPatient & UpdatePatient API Request schema:
    PatientGUID:
      existingCustomer.scriptcoPatientGuid ||
      existingCustomer.scriptCoPatientGuid ||
      existingCustomer.patientGUID ||
      existingCustomer.PatientGUID ||
      scriptCoDetails.scriptcoPatientGuid ||
      scriptCoDetails.scriptCoPatientGuid ||
      scriptCoDetails.patientGUID ||
      scriptCoDetails.PatientGUID ||
      opportunity?.scriptcoPatientGuid ||
      opportunity?.scriptCoPatientGuid ||
      opportunity?.patientGUID ||
      opportunity?.PatientGUID ||
      member?.scriptcoPatientGuid ||
      member?.scriptCoPatientGuid ||
      member?.patientGUID ||
      member?.PatientGUID ||
      null,
    MemberId: parsedMemberId,
    PatientDocument: patientDocPayload,
    FirstName: firstName,
    LastName: lastName,
    Email: general.email || existing.email || member.email || "",
    Phone: cleanPhoneNumber(
      general.mobileNumber || member.phoneNumber || member.phone || "",
    ),
    Dob: formattedDob,
    Gender: gender,
    AddressLine1: addressLine1,
    AddressLine2: addressLine2 && addressLine2 !== "-" ? addressLine2 : null,
    CountryCode: "US",
    StateCode: stateCode,
    CityName: cityName,
    ZipCode: zipCode,
    MedicalCondition: toCsv(medical.conditions) || "None",
    CurrentCondition: toCsv(medical.medications) || "None",
    Allergies: toCsv(medical.allergies) || "None",
    IsBillingAddress: resolveIsBillingAddress(formData, scriptCoDetails),
    BinNumber: pickNonEmpty(
      scriptCoDetails.binNumber,
      existingCustomer.BinNumber || "010101",
    ),
    PcnNumber: pickNonEmpty(
      scriptCoDetails.pcnNumber,
      existingCustomer.PcnNumber || "000",
    ),
    InsuranceName: pickNonEmpty(
      scriptCoDetails.insuranceName,
      (existingCustomer.InsuranceName || defaultGroupName || "").slice(0, 25),
    ),
    ScriptcoPatientSourceType: pickDefined(
      scriptCoDetails.scriptcoPatientSourceType,
      existingCustomer.ScriptcoPatientSourceType || "International",
    ),
    PatientId: patientId,
    PatientGroup: String(patientGroup || "").slice(0, 14),
    PatientNotes: resolvedPatientNotes,
    patientNotes: resolvedPatientNotes,

    // Backward-compatible camelCase fields
    firstName,
    lastName,
    dateOfBirth: formattedDob,
    sex: gender,
  };

  const isMale = payload.sex === "Male";
  if (!isMale) {
    payload.isPregnant = yesNoToBool(
      medical.pregnant,
      existingCustomer.isPregnant || false,
    );
    payload.pregnancyDueDate =
      convertToIsoDate(medical.pregnancyDueDate) ||
      convertToIsoDate(existingCustomer.pregnancyDueDate) ||
      null;
    payload.isBreastfeeding = yesNoToBool(
      medical.breastFeeding,
      existingCustomer.isBreastfeeding || false,
    );
  }

  return payload;
};

const isAddressValid = (addr) => {
  return !!(addr && (addr.line1 || addr.street || addr.city));
};

const parseScriptCoCommentText = (commentText) => {
  const parsedData = {
    physician: {},
    opportunity: {},
    acknowledgements: {},
    preferences: {},
    customer: {},
  };

  if (!commentText || typeof commentText !== "string") {
    return parsedData;
  }

  const sections = commentText.split(" | ");
  for (const section of sections) {
    const colonIdx = section.indexOf(":");
    if (colonIdx === -1) continue;

    const sectionName = section.substring(0, colonIdx).trim();
    const sectionContent = section.substring(colonIdx + 1).trim();

    const pairs = sectionContent.split(", ");
    const parsedPairs = {};
    for (const pair of pairs) {
      const eqIdx = pair.indexOf("=");
      if (eqIdx === -1) continue;
      const key = pair.substring(0, eqIdx).trim();
      const val = pair.substring(eqIdx + 1).trim();
      parsedPairs[key] = val;
    }

    if (sectionName === "prescribingPhysician") {
      parsedData.physician = {
        name: parsedPairs.name || "",
        npi: parsedPairs.npi || "",
        phone: parsedPairs.phone || "",
        fax: parsedPairs.fax || "",
      };
    } else if (sectionName === "orderDetailsReadOnly") {
      parsedData.opportunity = {
        newMedication: parsedPairs.newMedication || "Yes",
        paEffDate: parsedPairs.paEffDate || "01/01/2026",
        paTermDate: parsedPairs.paTermDate || "01/01/2026",
        productName: parsedPairs.productName || "",
        strength: parsedPairs.strength || "",
        qty: parsedPairs.qty || "",
      };
    } else if (sectionName === "acknowledgements") {
      parsedData.acknowledgements = {
        attested: parsedPairs.attested === "Yes",
        advocateName: parsedPairs.advocateName || "",
        advocateEmail: parsedPairs.advocateEmail || "",
        advocatePhone: parsedPairs.advocatePhone || "",
      };
    } else if (sectionName === "orderDetails") {
      parsedData.preferences = {
        quantity: parsedPairs.quantity || "",
        daysSupply: parsedPairs.daysSupply || "",
      };
    } else if (sectionName === "customerDetails") {
      parsedData.customer = {
        alternateEmail: parsedPairs.alternateEmail || "",
        groupId: parsedPairs.groupId || parsedPairs.insurancePlanId || "",
        insurancePlanId: parsedPairs.insurancePlanId || "",
        relationshipCode: parsedPairs.relationshipCode || "",
      };
    }
  }

  return parsedData;
};

const getLatestCommentText = (comments) => {
  if (!Array.isArray(comments) || comments.length === 0) return "";
  const sorted = [...comments].sort((a, b) => {
    const timeA = a.dateCreated ? Date.parse(a.dateCreated) : 0;
    const timeB = b.dateCreated ? Date.parse(b.dateCreated) : 0;
    const valA = Number.isNaN(timeA) ? 0 : timeA;
    const valB = Number.isNaN(timeB) ? 0 : timeB;
    return valB - valA;
  });
  return sorted[0]?.text || "";
};

const buildInitialUploadedFiles = (prescriptionDocs) => {
  const initialUploadedFiles = {};
  if (!Array.isArray(prescriptionDocs)) return initialUploadedFiles;
  prescriptionDocs.forEach((doc, idx) => {
    if (doc && (doc.Content || doc.content || doc.FileName || doc.fileName)) {
      const fileName =
        doc.FileName || doc.fileName || `prescription_${idx + 1}.png`;
      const base64 = doc.Content || doc.content || "";
      const fileKey = `rx_doc_${idx}`;
      initialUploadedFiles[fileKey] = {
        id: fileKey,
        fileName: fileName,
        name: fileName,
        base64: base64,
        content: base64,
        fileType: fileName.split(".").pop().toLowerCase() || "png",
        size: 0,
      };
    }
  });
  return initialUploadedFiles;
};

const resolveAttested = (parsedComment, memberAcknowledgements) => {
  if (typeof parsedComment.acknowledgements?.attested === "boolean") {
    return parsedComment.acknowledgements.attested;
  }
  if (typeof memberAcknowledgements.attested === "boolean") {
    return memberAcknowledgements.attested;
  }
  return false;
};

const getValidAddressField = (address, keys) =>
  isAddressValid(address)
    ? keys.map((key) => address[key]).find(Boolean)
    : undefined;

const buildBillingAddress = (customerDetails, opportunity, member) => {
  const fromAddresses = (...keys) =>
    getValidAddressField(opportunity?.billingAddress, keys) ||
    getValidAddressField(customerDetails?.billingAddress, keys);

  return {
    street:
      customerDetails.addressLine1 ||
      customerDetails.addressLine_1 ||
      fromAddresses("line1") ||
      getValidAddressField(member?.address, ["line1", "street"]) ||
      (typeof member?.address === "string" ? member.address : "") ||
      "",
    line:
      customerDetails.addressLine2 ||
      customerDetails.addressLine_2 ||
      fromAddresses("line2") ||
      getValidAddressField(member?.address, ["line2", "line"]) ||
      "",
    city:
      customerDetails.cityName ||
      fromAddresses("city") ||
      getValidAddressField(member?.address, ["city"]) ||
      member?.city ||
      "",
    state:
      customerDetails.stateCode ||
      customerDetails.stateName ||
      fromAddresses("state") ||
      getValidAddressField(member?.address, ["state"]) ||
      member?.state ||
      "",
    zip:
      customerDetails.zipCode ||
      fromAddresses("zipCode", "zip") ||
      getValidAddressField(member?.address, ["postalCode", "zip"]) ||
      member?.zip ||
      "",
  };
};

const resolveContactMethod = (method, initialInfo, available) => {
  if (!method || !available.includes(method)) {
    return determinePreferredMethod(initialInfo, available, method);
  }
  return method;
};

const buildProofDocument = (customerDetails) => {
  const rawProofPath =
    customerDetails.patientProofPath || customerDetails.proofPath || "";
  const proofFileName =
    customerDetails.proofName ||
    customerDetails.proofOriginalName ||
    (rawProofPath ? rawProofPath.split("/").pop() : "") ||
    "";

  if (!proofFileName && !rawProofPath) return null;
  return {
    fileName: proofFileName || "patient_document.png",
    patientProofPath: rawProofPath,
    fileType:
      (proofFileName || rawProofPath).split(".").pop().toLowerCase() || "png",
    size: 0,
  };
};

const isScriptCoSuccess = (result) =>
  result &&
  !result.error &&
  (result.status === true ||
    result.statusCode === 200 ||
    result.success === true ||
    !!result.data?.patientId ||
    !!result.data?.patientGUID);

const hasCustomerPayloadChanged = (
  lastSubmittedPayload,
  payload,
  localOpportunity,
) => {
  if (lastSubmittedPayload) {
    return JSON.stringify(lastSubmittedPayload) !== JSON.stringify(payload);
  }
  const initialFormData = getInitialFormData(localOpportunity);
  const initialPayload = buildCreateScriptCoPayload(
    initialFormData,
    localOpportunity,
  );
  return JSON.stringify(initialPayload) !== JSON.stringify(payload);
};

const cleanScriptCoOrderId = (orderId) =>
  String(orderId)
    .replaceAll("#", "")
    .replaceAll(/SCRIPTCO|GRM/gi, "")
    .trim();

const buildPrescriptionDocuments = (formData) =>
  Object.values(formData.prescription?.uploadedFiles || {})
    .map((item) => ({
      Content: item.base64 || item.content || item.Content || "",
      FileName: item.fileName || item.name || item.FileName || "rx-1.pdf",
    }))
    .filter((doc) => doc.Content);

const resolvePatientGuid = (customerId, localOpportunity) =>
  (typeof customerId === "string" && customerId.includes("-")
    ? customerId
    : null) ||
  localOpportunity?.scriptcoPatientGuid ||
  localOpportunity?.scriptCoPatientGuid ||
  localOpportunity?.patientGUID ||
  localOpportunity?.PatientGUID ||
  localOpportunity?.member?.scriptcoPatientGuid ||
  localOpportunity?.member?.scriptCoPatientGuid ||
  localOpportunity?.member?.patientGUID ||
  localOpportunity?.member?.PatientGUID ||
  localOpportunity?.scriptCoDetails?.scriptcoPatientGuid ||
  localOpportunity?.scriptCoDetails?.scriptCoPatientGuid ||
  localOpportunity?.scriptCoDetails?.patientGUID ||
  localOpportunity?.scriptCoDetails?.PatientGUID ||
  customerId ||
  "";

const buildPhysicianDetails = (physician) => ({
  Name:
    physician.name ||
    physician.Name ||
    `${physician.firstName || ""} ${physician.lastName || ""}`.trim() ||
    "",
  NPI: physician.npi || physician.NPI || "",
  Phone: physician.phone || physician.Phone || "",
  Fax: physician.fax || physician.Fax || "",
});

const getInitialFormData = (opportunity) => {
  const member = opportunity?.member || {};
  const scriptCoRaw =
    opportunity?.scriptCoDetails || opportunity?.grmDetails || {};
  const scriptCoDetails =
    scriptCoRaw?.data?.[0] ||
    (Array.isArray(scriptCoRaw?.data)
      ? scriptCoRaw.data[0]
      : scriptCoRaw?.data) ||
    scriptCoRaw?.result?.[0] ||
    (Array.isArray(scriptCoRaw?.result)
      ? scriptCoRaw.result[0]
      : scriptCoRaw?.result) ||
    scriptCoRaw ||
    {};
  const customerDetails =
    scriptCoDetails?.customerDetails || scriptCoDetails || {};

  const orderDetails =
    opportunity?.scriptCoOrderDetails || opportunity?.grmOrderDetails || {};
  const rawOrderPayload =
    orderDetails?.createdOrderPayload ||
    orderDetails?.data?.createdOrderPayload ||
    orderDetails?.result?.createdOrderPayload ||
    orderDetails?.orderPayload ||
    {};
  const usPhysician =
    rawOrderPayload?.USPrescriptionPhysicianDetails ||
    orderDetails?.USPrescriptionPhysicianDetails ||
    {};
  const prescriptionDocs =
    rawOrderPayload?.PrescriptionDocument ||
    orderDetails?.PrescriptionDocument ||
    orderDetails?.prescriptionDocument ||
    [];
  const orderComment =
    rawOrderPayload?.OrderComment ||
    orderDetails?.OrderComment ||
    orderDetails?.orderComment ||
    "";

  const comments =
    orderDetails?.result?.comments || orderDetails?.comments || [];
  const latestCommentText = getLatestCommentText(comments) || orderComment;
  const parsedComment = parseScriptCoCommentText(latestCommentText);

  const initialUploadedFiles = buildInitialUploadedFiles(prescriptionDocs);

  const memberOrderDetails = member.orderDetails || member.order || {};
  const memberPhysician = member.physician || member.prescribingPhysician || {};
  const memberAcknowledgements =
    member.acknowledgements || member.acknowledgment || member.advocate || {};
  const attested = resolveAttested(parsedComment, memberAcknowledgements);

  const billingAddress = buildBillingAddress(
    customerDetails,
    opportunity,
    member,
  );

  const deliveryAddress = { ...billingAddress };
  const firstProductQuantity = Array.isArray(rawOrderPayload?.ProductQuantity)
    ? rawOrderPayload.ProductQuantity[0]
    : rawOrderPayload?.ProductQuantity;

  const conditions = splitToArray(
    customerDetails.medicalCondition ||
      customerDetails.currentMedicalConditions ||
      member.currentMedicalConditions,
  );
  const medications = splitToArray(
    customerDetails.currentCondition ||
      customerDetails.currentMedicationsAndSupplements ||
      member.currentMedicationsAndSupplements,
  );

  const initialMobile =
    customerDetails.phone ||
    customerDetails.mobilePhone ||
    member.mobilePhone ||
    member.phoneNumber ||
    member.phone ||
    "";
  const initialAltPhone =
    customerDetails.homePhone ||
    member.alternatePhone ||
    member.alternatePhoneNum ||
    member.altPhone ||
    "";
  const initialEmail =
    customerDetails.email || member.email || member.contact?.email || "";
  const initialAltEmail =
    parsedComment.customer?.alternateEmail ||
    customerDetails.alternateEmail ||
    member.alternateEmail ||
    member.alternateEmailAddress ||
    member.altEmail ||
    "";

  const initialInfo = {
    mobileNumber: initialMobile,
    alternatePhone: initialAltPhone,
    email: initialEmail,
    alternateEmail: initialAltEmail,
  };

  const available = getAvailableContactMethods(initialInfo);

  const contactMethod = resolveContactMethod(
    opportunity?.preferredCommunicationMethod ||
      toLabelContactMethod(customerDetails?.preferredCommunicationMethod),
    initialInfo,
    available,
  );

  const refillContactMethod = resolveContactMethod(
    opportunity?.preferredCommunicationMethodForAutoRefills ||
      toLabelContactMethod(
        customerDetails?.preferredCommunicationMethodForAutoRefills,
      ),
    initialInfo,
    available,
  );

  const docObj = buildProofDocument(customerDetails);

  const defaultMemberId =
    customerDetails.patientId ||
    customerDetails.PatientId ||
    member.subscriberNum ||
    member.subscriberNumber ||
    member.memberId ||
    opportunity?.subscriberNum ||
    opportunity?.subscriberNumber ||
    opportunity?.memberId ||
    member.id ||
    opportunity?.id ||
    "";

  const defaultGroupName =
    customerDetails.patientGroup ||
    customerDetails.PatientGroup ||
    member.groupName ||
    member.groupNum ||
    member.groupNumber ||
    member.group ||
    opportunity?.groupName ||
    opportunity?.groupNum ||
    opportunity?.groupNumber ||
    opportunity?.group ||
    "";

  const scriptCoDetailsObj = {
    patientId: defaultMemberId,
    patientGroup: String(defaultGroupName || "").slice(0, 14),
    insuranceName: (
      customerDetails.insuranceName ||
      customerDetails.InsuranceName ||
      defaultGroupName ||
      ""
    ).slice(0, 25),
    binNumber:
      customerDetails.binNumber || customerDetails.BinNumber || "010101",
    pcnNumber: customerDetails.pcnNumber || customerDetails.PcnNumber || "000",
    scriptcoPatientSourceType:
      customerDetails.scriptcoPatientSourceType ||
      customerDetails.ScriptcoPatientSourceType ||
      "International",
    isBillingAddress: resolveIsBillingAddressV2(customerDetails),
    patientNotes:
      customerDetails.patientNotes || customerDetails.PatientNotes || "",
  };

  return {
    patientDocument: docObj,
    scriptCoDetails: scriptCoDetailsObj,
    address: {
      patientDocument: docObj,
      scriptCoDetails: scriptCoDetailsObj,
      general: {
        mobileNumber: initialMobile,
        alternatePhone: initialAltPhone,
        email: initialEmail,
        alternateEmail: initialAltEmail,
        contactMethod,
        refillContactMethod,
      },
      billing: billingAddress,
      delivery: deliveryAddress,
      copyBilling: true,
    },
    orderDetails: {
      scriptCoDetails: scriptCoDetailsObj,
      medical: {
        height: customerDetails.heightInCm
          ? `${customerDetails.heightInCm}cm`
          : "",
        weight: customerDetails.weightInKg
          ? `${customerDetails.weightInKg}kg`
          : "",
        pregnant: boolToYesNo(customerDetails.isPregnant, "No"),
        breastFeeding: boolToYesNo(customerDetails.isBreastfeeding, "No"),
        smoker: boolToYesNo(customerDetails.isSmoker, "No"),
        conditions,
        medications,
        allergies:
          customerDetails.allergies ||
          customerDetails.drugAllergies ||
          member.drugAllergies ||
          member.allergies ||
          member.allergy ||
          "",
        sex: capitalizeFirstLetter(
          customerDetails.gender ||
            customerDetails.sex ||
            member.sex ||
            member.gender ||
            "",
        ),
        pregnancyDueDate: convertToMmDdYyyy(
          customerDetails.pregnancyDueDate || "",
        ),
      },
      opportunity: {
        newMedication:
          parsedComment.opportunity?.newMedication ||
          memberOrderDetails.newMedication ||
          member.newMedication ||
          opportunity?.newMedication ||
          "Yes",
        paEffDate:
          parsedComment.opportunity?.paEffDate ||
          memberOrderDetails.paEffDate ||
          member.paEffDate ||
          opportunity?.paEffDate ||
          "01/01/2026",
        paTermDate:
          parsedComment.opportunity?.paTermDate ||
          memberOrderDetails.paTermDate ||
          member.paTermDate ||
          opportunity?.paTermDate ||
          "01/01/2026",
        quantity:
          parsedComment.opportunity?.qty ||
          memberOrderDetails.quantity ||
          member.quantity ||
          opportunity?.metricQty ||
          "",
        daysSupply:
          memberOrderDetails.daysSupply ||
          member.daysSupply ||
          opportunity?.daySupply ||
          "",
      },
      physician: {
        name:
          usPhysician.Name ||
          usPhysician.name ||
          parsedComment.physician?.name ||
          (memberPhysician.firstName
            ? `${memberPhysician.firstName} ${memberPhysician.lastName || ""}`.trim()
            : memberPhysician.name || opportunity?.physicianName || ""),
        npi:
          usPhysician.NPI ||
          usPhysician.npi ||
          parsedComment.physician?.npi ||
          memberPhysician.npi ||
          opportunity?.npi ||
          "",
        phone:
          usPhysician.Phone ||
          usPhysician.phone ||
          parsedComment.physician?.phone ||
          memberPhysician.phone ||
          opportunity?.phone ||
          "",
        fax:
          usPhysician.Fax ||
          usPhysician.fax ||
          parsedComment.physician?.fax ||
          memberPhysician.fax ||
          opportunity?.fax ||
          "",
      },
    },
    prescription: {
      documents: {
        prescriptionRx: "Optional",
        proofOfResidency: "Optional",
        driversLicence: "Optional",
        insuranceCard: "Optional",
        priorAuth: "Optional",
      },
      uploadedFiles: initialUploadedFiles,
      acknowledgements: {
        attested,
        advocateName:
          parsedComment.acknowledgements?.advocateName ||
          memberAcknowledgements.advocateName ||
          member.assignee ||
          "",
        advocateEmail:
          parsedComment.acknowledgements?.advocateEmail ||
          memberAcknowledgements.advocateEmail ||
          member.assigneeEmail ||
          "",
        advocatePhone:
          parsedComment.acknowledgements?.advocatePhone ||
          memberAcknowledgements.advocatePhone ||
          member.assigneePhone ||
          "",
      },
      preferences: {
        productQuantity:
          firstProductQuantity ??
          parsedComment.preferences?.productQuantity ??
          parsedComment.preferences?.quantity ??
          parsedComment.opportunity?.qty ??
          memberOrderDetails.quantity ??
          opportunity?.metricQty ??
          "",
        quantity:
          firstProductQuantity ??
          parsedComment.preferences?.quantity ??
          parsedComment.opportunity?.qty ??
          memberOrderDetails.quantity ??
          opportunity?.metricQty ??
          "",
        shippingQuantity:
          rawOrderPayload?.ShippingQuantity ??
          rawOrderPayload?.shippingQuantity ??
          orderDetails?.ShippingQuantity ??
          orderDetails?.shippingQuantity ??
          parsedComment.preferences?.shippingQuantity ??
          opportunity?.shippingQuantity ??
          opportunity?.ShippingQuantity ??
          "",
        shippingId:
          rawOrderPayload?.ShippingId ??
          rawOrderPayload?.shippingId ??
          orderDetails?.ShippingId ??
          orderDetails?.shippingId ??
          parsedComment.preferences?.shippingId ??
          opportunity?.shippingId ??
          opportunity?.shippingMethodId ??
          1,
        daysSupply:
          parsedComment.preferences?.daysSupply || opportunity?.daySupply || "",
        orderComment:
          orderComment && !orderComment.includes("prescribingPhysician:")
            ? orderComment
            : "",
        customOrderNumber:
          rawOrderPayload?.CustomOrderNumber ||
          rawOrderPayload?.customOrderNumber ||
          orderDetails?.CustomOrderNumber ||
          orderDetails?.customOrderNumber ||
          "",
        counselling:
          rawOrderPayload?.Counselling ??
          rawOrderPayload?.counselling ??
          orderDetails?.Counselling ??
          orderDetails?.counselling ??
          1,
        orderDate:
          rawOrderPayload?.OrderDate ||
          orderDetails?.OrderDate ||
          convertToMmDdYyyy(orderDetails?.submittedOn) ||
          "",
        orderId:
          orderDetails?.orderNumber ||
          orderDetails?.orderId ||
          orderDetails?.id ||
          opportunity?.vendorReferenceId ||
          "",
        orderStatus:
          typeof orderDetails?.orderStatus === "object"
            ? orderDetails?.orderStatus?.orderStatus
            : orderDetails?.orderStatus || orderDetails?.status || "",
      },
    },
  };
};

const ScriptCoEnrollmentModal = ({
  open,
  onClose,
  opportunity,
  onSuccess,
  onMemberDetailsRefresh,
}) => {
  const [fetchedOrderDetails, setFetchedOrderDetails] = useState(null);

  const localOpportunity = React.useMemo(() => {
    const activeOpp = fetchedOrderDetails
      ? { ...opportunity, scriptCoOrderDetails: fetchedOrderDetails }
      : opportunity;

    const orderDetails =
      activeOpp?.scriptCoOrderDetails || activeOpp?.grmOrderDetails || {};
    const comments =
      orderDetails?.result?.comments || orderDetails?.comments || [];
    const latestCommentText = getLatestCommentText(comments);
    if (!latestCommentText) return activeOpp;
    const parsedComment = parseScriptCoCommentText(latestCommentText);
    return {
      ...activeOpp,
      drug: parsedComment.opportunity?.productName || activeOpp?.drug,
      strength: parsedComment.opportunity?.strength || activeOpp?.strength,
      metricQty: parsedComment.opportunity?.qty || activeOpp?.metricQty,
    };
  }, [opportunity, fetchedOrderDetails]);

  const [activeStep, setActiveStep] = useState(0);
  const [customerId, setCustomerId] = useState(
    () =>
      localOpportunity?.scriptCoDetails?.id ||
      localOpportunity?.grmDetails?.id ||
      null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [lastSubmittedPayload, setLastSubmittedPayload] = useState(null);
  const [stepsValidation, setStepsValidation] = useState({
    0: false, // Customer Info
    1: false, // Order Details
    2: false, // Prescription
  });
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successType, setSuccessType] = useState(""); // "submit" or "update"

  const showError = useCallback((msg, showModal = true) => {
    setSubmissionError(msg);
    if (showModal) {
      setErrorModalOpen(true);
    }
  }, []);

  const contentRef = useRef(null);

  // Clear submission error when activeStep changes
  useEffect(() => {
    setSubmissionError("");
  }, [activeStep]);

  // Reset to first step and clear errors when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setShowErrors(false);
      setErrorModalOpen(false);
      setSuccessModalOpen(false);
      setSuccessType("");
      setSubmissionError("");
    }
  }, [open]);

  // Scroll to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  // Consolidated form data to persist across steps
  const [formData, setFormData] = useState(() =>
    getInitialFormData(localOpportunity),
  );

  const getProductsList = (productSearchResult) => {
    if (Array.isArray(productSearchResult)) {
      return productSearchResult;
    }
    return (
      productSearchResult?.result ||
      productSearchResult?.data ||
      (productSearchResult?.productOptionId ? [productSearchResult] : [])
    );
  };

  useEffect(() => {
    if (open) {
      const initialFormData = getInitialFormData(localOpportunity);
      setFormData(initialFormData);

      const resolvedPatientGuid =
        localOpportunity?.scriptcoPatientGuid ||
        localOpportunity?.scriptCoPatientGuid ||
        localOpportunity?.patientGUID ||
        localOpportunity?.PatientGUID ||
        localOpportunity?.member?.scriptcoPatientGuid ||
        localOpportunity?.member?.scriptCoPatientGuid ||
        localOpportunity?.member?.patientGUID ||
        localOpportunity?.member?.PatientGUID ||
        localOpportunity?.scriptCoDetails?.scriptcoPatientGuid ||
        localOpportunity?.scriptCoDetails?.scriptCoPatientGuid ||
        localOpportunity?.scriptCoDetails?.patientGUID ||
        localOpportunity?.scriptCoDetails?.PatientGUID ||
        null;

      setCustomerId(resolvedPatientGuid);
      setLastSubmittedPayload(null);
      setStepsValidation({
        0: false,
        1: false,
        2: false,
      });

      const fetchProductDetails = async () => {
        const ndcValue =
          localOpportunity?.ndc ||
          localOpportunity?.drugNdc ||
          localOpportunity?.drug_ndc ||
          localOpportunity?.ndcCode ||
          "";
        let productSearchResult = localOpportunity?.scriptCoSearchResult;
        if (!productSearchResult && ndcValue) {
          try {
            productSearchResult = await searchScriptCoProducts({
              searchTerm: ndcValue,
              ndc: ndcValue,
            });
          } catch (err) {
            console.error("Failed to fetch product details for prefill:", err);
          }
        }

        if (productSearchResult) {
          const productsList = getProductsList(productSearchResult);

          if (productsList && productsList.length > 0) {
            const firstProduct = productsList[0];
            const pId =
              firstProduct.productID ||
              firstProduct.productOptionId ||
              firstProduct.productId ||
              firstProduct.id ||
              "";
            const pQty =
              firstProduct.minimumOrderQty ||
              firstProduct.productQuantity ||
              firstProduct.packSize ||
              localOpportunity?.metricQty ||
              1;

            setFormData((prev) => ({
              ...prev,
              prescription: {
                ...prev.prescription,
                preferences: {
                  ...prev.prescription?.preferences,
                  productId: pId,
                  productQuantity: pQty,
                },
              },
            }));
          }
        }
      };

      void fetchProductDetails();
    }
  }, [open, localOpportunity]);

  const handleDataChange = useCallback((stepKey, sectionKey, newData) => {
    setFormData((prev) => {
      const updatedStep = {
        ...prev[stepKey],
        [sectionKey]: newData,
      };

      if (sectionKey === "scriptCoDetails") {
        const mergedScriptCoDetails = {
          ...prev.scriptCoDetails,
          ...(stepKey === "address"
            ? prev.orderDetails?.scriptCoDetails
            : prev.address?.scriptCoDetails),
          ...newData,
        };
        return {
          ...prev,
          scriptCoDetails: mergedScriptCoDetails,
          [stepKey]: updatedStep,
        };
      }

      return {
        ...prev,
        [stepKey]: updatedStep,
      };
    });
  }, []);

  const handleAddressDataChange = useCallback(
    (section, data) => {
      handleDataChange("address", section, data);
    },
    [handleDataChange],
  );

  const handleOrderDetailsDataChange = useCallback(
    (section, data) => {
      handleDataChange("orderDetails", section, data);
    },
    [handleDataChange],
  );

  const handlePrescriptionDataChange = useCallback(
    (section, data) => {
      handleDataChange("prescription", section, data);
    },
    [handleDataChange],
  );

  const setIsStepValid = useCallback(
    (isValid) => {
      setStepsValidation((prev) => {
        if (prev[activeStep] === isValid) return prev;
        return { ...prev, [activeStep]: isValid };
      });
    },
    [activeStep],
  );

  const editExistingCustomer = async (resolvedId, payload) => {
    const hasDetailsChanged = hasCustomerPayloadChanged(
      lastSubmittedPayload,
      payload,
      localOpportunity,
    );

    if (!hasDetailsChanged) {
      console.log(
        "No details changed for existing customer. Going to next step without API call.",
      );
      setCustomerId(resolvedId);
      setLastSubmittedPayload(payload);
      setActiveStep((prev) => prev + 1);
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Details changed for existing customer. Calling edit API...");
      const editResult = await editScriptCoCustomer(payload);

      if (!isScriptCoSuccess(editResult)) {
        const msg =
          editResult?.message ||
          editResult?.error ||
          editResult?.statusMessage ||
          "Failed to update customer details in ScriptCo";
        throw new Error(msg);
      }
      console.log("Customer details updated successfully.");
      setCustomerId(resolvedId);
      setLastSubmittedPayload(payload);
      setActiveStep((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to update customer details:", error);
      showError(
        error.message || "Failed to update customer details in ScriptCo",
        false,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const createNewCustomer = async (payload) => {
    setIsSubmitting(true);
    try {
      console.log("No existing customer found. Calling create API...");
      const createResult = await createScriptCoCustomer(payload);

      if (!isScriptCoSuccess(createResult)) {
        const msg =
          createResult?.message ||
          createResult?.error ||
          createResult?.statusMessage ||
          "Failed to create customer in ScriptCo";
        throw new Error(msg);
      }

      const resolvedCustomerId =
        createResult.data?.scriptcoPatientGuid ||
        createResult.data?.scriptCoPatientGuid ||
        createResult.data?.patientGUID ||
        createResult.data?.PatientGUID ||
        createResult.scriptcoPatientGuid ||
        createResult.scriptCoPatientGuid ||
        createResult.patientGUID ||
        createResult.PatientGUID ||
        createResult.result?.scriptcoPatientGuid ||
        createResult.result?.scriptCoPatientGuid ||
        createResult.result?.patientGUID ||
        createResult.result?.PatientGUID ||
        createResult.customerAccount?.scriptcoPatientGuid ||
        createResult.customerAccount?.scriptCoPatientGuid ||
        createResult.customerAccount?.patientGUID ||
        createResult.customerAccount?.PatientGUID ||
        createResult.customerDetails?.scriptcoPatientGuid ||
        createResult.customerDetails?.scriptCoPatientGuid ||
        createResult.customerDetails?.patientGUID ||
        createResult.customerDetails?.PatientGUID ||
        createResult.data?.patientId ||
        createResult.data?.id ||
        createResult.patientId ||
        createResult.id ||
        null;

      console.log("Customer created with ID:", resolvedCustomerId);
      setCustomerId(resolvedCustomerId);
      setLastSubmittedPayload(payload);

      const memberIdToFetch =
        localOpportunity?.member?.memberId ||
        localOpportunity?.member?.id ||
        (localOpportunity?.memberId !== localOpportunity?.id &&
        localOpportunity?.memberId !== localOpportunity?.opportunityId
          ? localOpportunity?.memberId
          : null) ||
        payload.MemberId;

      if (memberIdToFetch) {
        try {
          console.log(
            "Calling fetchMemberOverview for memberId:",
            memberIdToFetch,
          );
          const memberOverview = await fetchMemberOverview(memberIdToFetch);
          console.log(
            "Fetched member overview after customer creation:",
            memberOverview,
          );

          const fetchedGuid =
            memberOverview?.scriptcoPatientGuid ||
            memberOverview?.scriptCoPatientGuid ||
            memberOverview?.patientGUID ||
            memberOverview?.PatientGUID ||
            memberOverview?.data?.scriptcoPatientGuid ||
            memberOverview?.data?.scriptCoPatientGuid ||
            memberOverview?.data?.patientGUID ||
            memberOverview?.data?.PatientGUID;

          if (fetchedGuid) {
            console.log(
              "Setting customerId from fetched memberOverview scriptcoPatientGuid:",
              fetchedGuid,
            );
            setCustomerId(fetchedGuid);
          }

          if (onMemberDetailsRefresh) {
            onMemberDetailsRefresh(memberOverview);
          }
        } catch (fetchErr) {
          console.error(
            "Failed to fetch member overview after customer creation:",
            fetchErr,
          );
        }
      }

      setActiveStep((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to create customer:", error);
      showError(
        error.message || "Failed to create customer in ScriptCo",
        false,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomerSubmit = async () => {
    const payload = buildCreateScriptCoPayload(formData, localOpportunity);

    const hasOrderId = Boolean(
      localOpportunity?.vendorReferenceId ||
      localOpportunity?.vendorPreferenceId ||
      localOpportunity?.orderId ||
      localOpportunity?.scriptCoOrderDetails?.orderNumber ||
      localOpportunity?.scriptCoOrderDetails?.id ||
      formData?.prescription?.preferences?.orderId,
    );

    const existingGuid =
      payload?.PatientGUID ||
      localOpportunity?.scriptcoPatientGuid ||
      localOpportunity?.scriptCoPatientGuid ||
      localOpportunity?.patientGUID ||
      localOpportunity?.PatientGUID ||
      localOpportunity?.member?.scriptcoPatientGuid ||
      localOpportunity?.member?.scriptCoPatientGuid ||
      localOpportunity?.member?.patientGUID ||
      localOpportunity?.member?.PatientGUID ||
      localOpportunity?.scriptCoDetails?.scriptcoPatientGuid ||
      localOpportunity?.scriptCoDetails?.scriptCoPatientGuid ||
      localOpportunity?.scriptCoDetails?.patientGUID ||
      localOpportunity?.scriptCoDetails?.PatientGUID ||
      null;

    const resolvedId = customerId || existingGuid;

    if (hasOrderId || payload.PatientGUID || resolvedId) {
      await editExistingCustomer(resolvedId || "EXISTING_CUSTOMER", payload);
    } else {
      await createNewCustomer(payload);
    }
  };

  const resolveProductOptionId = async () => {
    let productOptionId =
      formData?.prescription?.preferences?.productId ||
      localOpportunity?.scriptCoProductOptionId ||
      localOpportunity?.grmProductOptionId;

    if (productOptionId) {
      return productOptionId;
    }

    if (localOpportunity?.scriptCoSearchResult) {
      const productsList = getProductsList(
        localOpportunity.scriptCoSearchResult,
      );
      if (productsList && productsList.length > 0) {
        return (
          productsList[0].productID ||
          productsList[0].productOptionId ||
          productsList[0].productId ||
          productsList[0].id ||
          null
        );
      }
    }

    const ndcValue =
      localOpportunity?.ndc ||
      localOpportunity?.drugNdc ||
      localOpportunity?.drug_ndc ||
      localOpportunity?.ndcCode ||
      "";
    if (!ndcValue) {
      return null;
    }

    try {
      const productSearchPayload = { searchTerm: ndcValue, ndc: ndcValue };
      const productSearchResult =
        await searchScriptCoProducts(productSearchPayload);
      const productsList = getProductsList(productSearchResult);

      if (productsList && productsList.length > 0) {
        return (
          productsList[0].productID ||
          productsList[0].productOptionId ||
          productsList[0].productId ||
          productsList[0].id ||
          null
        );
      }
    } catch (err) {
      console.error("resolveProductOptionId search failed:", err);
    }
    return null;
  };

  const fetchAndLogOrderDetails = async (orderId) => {
    if (!orderId) {
      console.warn("Order created but no order ID returned to fetch history.");
      return;
    }
    try {
      console.log("Fetching order details for orderId:", orderId);
      const orderDetails = await getScriptCoOrderDetails(orderId);
      console.log("Order details (history):", orderDetails);
      if (orderDetails && !orderDetails.error) {
        setFetchedOrderDetails(orderDetails);
      }
    } catch (err) {
      console.error("Failed to fetch order details:", err);
    }
  };

  const updateExistingOrder = async (
    existingOrderId,
    orderPayload,
    preferences,
  ) => {
    const cleanOrderId = cleanScriptCoOrderId(existingOrderId);

    const payload = buildCreateScriptCoPayload(formData, localOpportunity);

    // 1. Check if customer details changed
    const hasCustomerDetailsChanged = hasCustomerPayloadChanged(
      lastSubmittedPayload,
      payload,
      localOpportunity,
    );

    if (hasCustomerDetailsChanged) {
      console.log(
        "Customer details changed for existing order. Calling edit customer API in step 3...",
      );
      const editResult = await editScriptCoCustomer(payload);

      if (!isScriptCoSuccess(editResult)) {
        const msg =
          editResult?.message ||
          editResult?.error ||
          editResult?.statusMessage ||
          "Failed to update customer details in ScriptCo";
        throw new Error(msg);
      }
      console.log("Customer details updated successfully in step 3.");
      setLastSubmittedPayload(payload);
    }

    // 2. Check if order comment/note was added
    const orderCommentText = orderPayload.OrderComment;
    const initialFormData = getInitialFormData(localOpportunity);
    const initialOrderComment =
      initialFormData?.prescription?.preferences?.orderComment || "";
    const commentChanged =
      preferences.orderComment &&
      preferences.orderComment !== initialOrderComment;

    let noteAdded = false;
    if (commentChanged && orderCommentText) {
      try {
        await addScriptCoOrderNote(cleanOrderId, {
          text: orderCommentText,
        });
        noteAdded = true;
      } catch (noteErr) {
        console.warn("Failed to add order note:", noteErr);
      }
    }

    // 3. If no changes were made to customer details or comment/note, close modal and refresh opportunities
    if (!hasCustomerDetailsChanged && !noteAdded) {
      console.log(
        "No changes made to existing order. Closing modal and reloading opportunities...",
      );
      onClose();
      if (onSuccess) onSuccess();
      return;
    }

    if (hasCustomerDetailsChanged) {
      setSuccessType("customer_update");
    } else {
      setSuccessType("update");
    }
    setSuccessModalOpen(true);
  };

  const addVendorReferenceNote = async (vendorReferenceId, noteText) => {
    if (!vendorReferenceId) return;
    try {
      const cleanOrderId = cleanScriptCoOrderId(vendorReferenceId);
      if (noteText) {
        await addScriptCoOrderNote(cleanOrderId, { text: noteText });
      }
    } catch (noteErr) {
      console.warn("Failed to add order note:", noteErr);
    }
  };

  const handleFinalOrderSubmit = async () => {
    setIsSubmitting(true);
    try {
      const productOptionId = await resolveProductOptionId();
      const hasVendorRefId =
        localOpportunity?.vendorReferenceId ||
        localOpportunity?.vendorPreferenceId ||
        localOpportunity?.program?.vendorReferenceId;

      if (!productOptionId && !hasVendorRefId) {
        throw new Error("Could not find a matching product from search.");
      }

      const memberProgramId =
        localOpportunity?.programId ||
        localOpportunity?.program?.id ||
        localOpportunity?.memberProgramId ||
        localOpportunity?.id ||
        "";

      const physician =
        formData.prescription?.physician ||
        formData.orderDetails?.physician ||
        {};
      const preferences = formData.prescription?.preferences || {};

      const prescriptionDocuments = buildPrescriptionDocuments(formData);

      const resolvedPatientGuid = resolvePatientGuid(
        customerId,
        localOpportunity,
      );

      const resolvedProductId =
        productOptionId ||
        preferences.productId ||
        localOpportunity?.scriptCoProductOptionId ||
        localOpportunity?.grmProductOptionId ||
        "";

      const orderPayload = {
        PatientGUID: resolvedPatientGuid,
        ProductId: resolvedProductId ? [Number(resolvedProductId)] : [],
        ProductQuantity: [
          Number(
            preferences.productQuantity ??
              preferences.quantity ??
              localOpportunity?.metricQty ??
              1,
          ),
        ],
        ShippingId: 1,
        ShippingQuantity: 1,
        OrderComment: preferences.orderComment
          ? String(preferences.orderComment).trim()
          : "",
        Counselling: 1,
        AddRemoteDataLogger: Boolean(preferences.addRemoteDataLogger),
        ...(preferences.customOrderNumber && {
          CustomOrderNumber: preferences.customOrderNumber,
        }),
        ...(preferences.couponCode && {
          CouponCode: preferences.couponCode,
        }),
        USPrescriptionPhysicianDetails: buildPhysicianDetails(physician),
        OrderDate: new Date().toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        }),
        PrescriptionDocument: prescriptionDocuments,
      };

      const existingOrderId =
        localOpportunity?.vendorReferenceId ||
        localOpportunity?.vendorPreferenceId ||
        localOpportunity?.orderId ||
        localOpportunity?.scriptCoOrderDetails?.orderNumber ||
        localOpportunity?.scriptCoOrderDetails?.id ||
        formData?.prescription?.preferences?.orderId;

      if (existingOrderId) {
        await updateExistingOrder(existingOrderId, orderPayload, preferences);
        return;
      }

      console.log(
        "Creating ScriptCo order for memberProgramId:",
        memberProgramId,
        "payload:",
        orderPayload,
      );
      const orderResult = await createScriptCoOrder(
        memberProgramId,
        orderPayload,
      );
      if (!orderResult || orderResult.success === false || orderResult.error) {
        throw new Error(
          orderResult?.message ||
            orderResult?.error ||
            "Failed to create order in ScriptCo",
        );
      }

      console.log("Order created successfully:", orderResult);

      await addVendorReferenceNote(
        localOpportunity?.vendorReferenceId,
        orderPayload.OrderComment,
      );

      const orderId =
        orderResult?.result?.id ||
        orderResult?.result?.[0]?.id ||
        orderResult?.data?.id ||
        orderResult?.id;
      await fetchAndLogOrderDetails(orderId);

      setSuccessType("submit");
      setSuccessModalOpen(true);
    } catch (error) {
      console.error("Enrollment submission failed:", error);
      showError(`Enrollment failed: ${error.message || error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allStepsValid =
    stepsValidation[0] === true &&
    stepsValidation[1] === true &&
    stepsValidation[2] === true;

  const isFinalStep = activeStep === steps.length - 1;
  // Only disable the Submit button (final step) — Next button is always clickable
  const isButtonDisabled = isSubmitting || (isFinalStep && !allStepsValid);

  let buttonText = "Next";
  if (isFinalStep) {
    buttonText = isSubmitting ? "Submitting..." : "Submit";
  }

  const getSuccessTitle = (successType) => {
    if (successType === "customer_update") return "Customer Details Updated";
    if (successType === "update") return "Order Updated";
    return "Order Submitted";
  };

  const getSuccessMessage = (successType) => {
    if (successType === "customer_update") {
      return "The customer details have been successfully updated in ScriptCo.";
    }
    if (successType === "update") {
      return "The updated information has been successfully submitted to ScriptCo for processing.";
    }
    return "The enrollment request has been successfully submitted to ScriptCo for processing.";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "90vw",
              lg: "80vw",
              xl: "75vw",
            },
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            maxHeight: "90vh",
            m: 1,
          },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "90vh",
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              color: "#10100F",
            }}
          >
            ScriptCo Enrollment - Step {activeStep + 1} of {steps.length}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {submissionError && (
              <Typography
                sx={{
                  color: "#E53935",
                  fontSize: "12px",
                  fontWeight: 500,
                  maxWidth: { xs: "250px", sm: "400px", md: "500px" },
                  wordBreak: "break-word",
                }}
              >
                {submissionError}
              </Typography>
            )}
            <IconButton
              onClick={onClose}
              sx={{ color: "#403C6F" }}
              size="small"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Sticky Header Content */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}>
          <ScriptCoMemberDetails opportunity={localOpportunity} />

          <Box sx={{ width: "95%", mx: "auto" }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                "& .MuiStepLabel-label": {
                  fontWeight: 500,
                  fontSize: "13px",
                  color: "#A09EBA",
                  marginTop: "6px",
                },
                "& .MuiStepLabel-label.Mui-active": {
                  color: "#403C6F",
                  fontWeight: 600,
                },
                "& .MuiStepLabel-iconContainer": {
                  padding: 0,
                },
                "& .MuiStepConnector-root": {
                  top: "6px",
                  left: "calc(-50% + 7px)",
                  right: "calc(50% + 7px)",
                },
                "& .MuiStepConnector-line": {
                  borderColor: "#C4C2D3",
                  borderTopWidth: 2,
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel slots={{ stepIcon: CustomStepIcon }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DialogContent
            ref={contentRef}
            sx={{
              p: 0,
              pb: 2,
              backgroundColor: "transparent",
              overflowX: "hidden",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              // custom scrollbar styling to make UI cleaner
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#D8D6E2",
                borderRadius: "4px",
              },
            }}
          >
            {/* Step Content Area */}
            <Box sx={{ flex: 1, minHeight: "350px", pr: 1 }}>
              {activeStep === 0 && (
                <ScriptCoAddressDetails
                  opportunity={localOpportunity}
                  data={formData.address}
                  onDataChange={handleAddressDataChange}
                  onValidationChange={setIsStepValid}
                  showErrors={showErrors}
                />
              )}
              {activeStep === 1 && (
                <ScriptCoOrderDetailsForm
                  opportunity={localOpportunity}
                  data={formData.orderDetails}
                  onDataChange={handleOrderDetailsDataChange}
                  onValidationChange={setIsStepValid}
                  showErrors={showErrors}
                />
              )}
              {activeStep === 2 && (
                <ScriptCoPrescriptionOpportunity
                  opportunity={localOpportunity}
                  data={formData.prescription}
                  onDataChange={handlePrescriptionDataChange}
                  onValidationChange={setIsStepValid}
                  showErrors={showErrors}
                  customerId={customerId}
                />
              )}
            </Box>
          </DialogContent>

          {/* Footer Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              pt: 2,
              pb: 2,
              px: 3,
              mx: -2,
              mb: -2,
              mt: "auto",
              backgroundColor: "#fff",
              borderBottomLeftRadius: "16px",
              borderBottomRightRadius: "16px",
            }}
          >
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={() => setActiveStep((prev) => prev - 1)}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  color: "#403C6F",
                  borderColor: "#9F9DB6",
                  px: 3,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "rgba(64, 60, 111, 0.04)",
                    borderColor: "#403C6F",
                  },
                }}
              >
                Back
              </Button>
            )}

            <Button
              variant="contained"
              onClick={async () => {
                if (stepsValidation[activeStep]) {
                  setShowErrors(false);
                } else {
                  setShowErrors(true);
                  return;
                }

                if (activeStep === 1) {
                  const hasOrderId = Boolean(
                    localOpportunity?.vendorReferenceId ||
                    localOpportunity?.vendorPreferenceId ||
                    localOpportunity?.orderId ||
                    localOpportunity?.scriptCoOrderDetails?.orderNumber ||
                    localOpportunity?.scriptCoOrderDetails?.id ||
                    formData?.prescription?.preferences?.orderId,
                  );

                  if (hasOrderId) {
                    setActiveStep((prev) => prev + 1);
                    return;
                  }

                  await handleCustomerSubmit();
                  return;
                }

                if (!isFinalStep) {
                  setActiveStep((prev) => prev + 1);
                  return;
                }

                await handleFinalOrderSubmit();
              }}
              disabled={isButtonDisabled}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 4,
                py: 1,
                boxShadow: "none",
                "&.Mui-disabled": {
                  backgroundColor: "rgba(64, 60, 111, 0.4)",
                  color: "rgba(255, 255, 255, 0.7)",
                },
                "&:hover": {
                  backgroundColor: "#312e5e",
                  boxShadow: "none",
                },
              }}
            >
              {buttonText}
            </Button>
          </Box>
        </LocalizationProvider>
      </Box>

      {/* Error Popup Modal */}
      <CommonDialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        variant="alert"
        icon={<ErrorOutlineIcon sx={{ color: "#E53935", fontSize: "32px" }} />}
        title="Submission Failed"
        actions={
          <Button
            variant="contained"
            onClick={() => setErrorModalOpen(false)}
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#403C6F",
              color: "#FFF",
              px: 6,
              py: 1,
              boxShadow: "none",
              width: "100%",
              maxWidth: "200px",
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
        }
      >
        An unexpected error occurred while submitting to ScriptCo, Please try
        again. If the issue persists, contact your system administrator.
      </CommonDialog>

      {/* Success Popup Modal */}
      <CommonDialog
        open={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          if (onSuccess) onSuccess();
          onClose();
        }}
        variant="alert"
        icon={<CheckIcon sx={{ color: "#137333", fontSize: "32px" }} />}
        iconBgColor="#E6F4EA"
        title={getSuccessTitle(successType)}
        actions={
          <Button
            variant="contained"
            onClick={() => {
              setSuccessModalOpen(false);
              if (onSuccess) onSuccess();
              onClose();
            }}
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#403C6F",
              color: "#FFF",
              px: 6,
              py: 1,
              boxShadow: "none",
              width: "100%",
              maxWidth: "200px",
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
        }
      >
        {getSuccessMessage(successType)}
      </CommonDialog>
    </Dialog>
  );
};

ScriptCoEnrollmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  opportunity: PropTypes.object,
  onSuccess: PropTypes.func,
  onMemberDetailsRefresh: PropTypes.func,
};

export default ScriptCoEnrollmentModal;
